import {useApolloClient} from '@apollo/client';
import {none} from '@danwang/option';
import {
  useMigrateRawTransactionsMutation,
  useRawTransactionMigrationPreviewQuery,
} from '@transactions/graphql';
import * as React from 'react';
import AccountSelect from 'src/AccountSelect';
import {Button} from 'src/Button';
import InputGroup from 'src/InputGroup';
import Loading from 'src/Loading';
import {MigrationOverlapPreview} from 'src/MigrationOverlapPreview';
import {MigrationTimeline} from 'src/MigrationTimeline';
import {Modal} from 'src/Modal';
import type {Option} from '@danwang/option';
import type {AccountListItemFragment} from '@transactions/graphql';
import type {SystemProps} from 'src/Modals';

export type MigrateAccountTransactionsModalUserProps = {
  /** The source account — all of its raw transactions will move. */
  account: {_id: string; name: string; currency: string};
  onMigrated?: () => void;
};

const formatRange = (
  oldest: string | null | undefined,
  newest: string | null | undefined,
): string => (oldest && newest ? `${oldest} – ${newest}` : '');

/**
 * Whole-account migration: moves ALL raw transactions from the source account
 * to a chosen destination (the "re-linked account" scenario, where history
 * should live under a new account id). The server computes a preview with
 * blockers — conditions under which migrating would corrupt reconciliations
 * or be undone by Plaid sync — and this modal surfaces them; the migration
 * cannot run while any blocker is present.
 */
export const MigrateAccountTransactionsModal = (
  props: MigrateAccountTransactionsModalUserProps & SystemProps,
) => {
  const formId = React.useId();
  const {account, onMigrated, onRequestClose} = props;
  const client = useApolloClient();
  const [selectedAccount, setSelectedAccount] =
    React.useState<Option<AccountListItemFragment>>(none);
  const [overlapConfirmed, setOverlapConfirmed] = React.useState(false);

  const destinationId = selectedAccount
    .map((destination) => destination._id)
    .getOrElse('');

  // network-only: the preview drives an irreversible action, so it must
  // reflect the current server state, not a cached page.
  const {
    data: previewData,
    loading: previewLoading,
    error: previewError,
  } = useRawTransactionMigrationPreviewQuery({
    variables: {fromAccountId: account._id, toAccountId: destinationId},
    skip: destinationId === '',
    fetchPolicy: 'network-only',
  });
  const preview = destinationId
    ? previewData?.rawTransactionMigrationPreview
    : undefined;

  const [migrateRawTransactions, {loading: migrating, error: migrateError}] =
    useMigrateRawTransactionsMutation();

  const handleSelectAccount = (
    destination: Option<AccountListItemFragment>,
  ) => {
    setSelectedAccount(destination);
    setOverlapConfirmed(false);
  };

  // Narrowed to a non-null pair so the overlap UI (warning text, transaction
  // preview) can use the window without re-null-checking each bound.
  const overlapWindow =
    preview?.overlapStartDate != null && preview?.overlapEndDate != null
      ? {start: preview.overlapStartDate, end: preview.overlapEndDate}
      : null;
  const hasOverlap = overlapWindow !== null;
  // Soft-deleted rows migrate too, so an account holding only deleted rows
  // is still migratable (that is what clears it for deletion). Likewise an
  // investment account whose only history is portfolio snapshots.
  const hasAnythingToMove =
    preview != null &&
    preview.count + preview.deletedCount + preview.snapshotCount > 0;
  const canMigrate =
    preview != null &&
    preview.blockers.length === 0 &&
    hasAnythingToMove &&
    (!hasOverlap || overlapConfirmed) &&
    !previewLoading &&
    !migrating;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canMigrate) {
      return;
    }
    selectedAccount.forEach((destination) => {
      migrateRawTransactions({
        variables: {
          fromAccountId: account._id,
          toAccountId: destination._id,
          force: overlapConfirmed,
        },
      })
        .then(async () => {
          // A whole-account move staleness far more than the transaction
          // lists (account transaction counts, balances, insights), so
          // refetch all active queries rather than evicting surgically. This
          // is a rare admin action; the coarse reset is acceptable. The
          // migration has already succeeded at this point, so a failed
          // refetch must not keep the modal open (or reject unhandled).
          await client.resetStore().catch(() => {});
          onMigrated?.();
          onRequestClose();
        })
        .catch(() => {
          // Mutation failures surface via migrateError.
        });
    });
  };

  const footer = (
    <>
      <Button variant="outline" disabled={migrating} onClick={onRequestClose}>
        Cancel
      </Button>
      <Button
        variant="primary"
        disabled={!canMigrate}
        type="submit"
        form={formId}
      >
        {preview && preview.count > 0
          ? `Migrate ${preview.count.toLocaleString()} transaction${
              preview.count === 1 ? '' : 's'
            }`
          : 'Migrate'}
      </Button>
    </>
  );

  return (
    <Modal
      onRequestClose={onRequestClose}
      header="Migrate all transactions"
      footer={footer}
    >
      <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-4">
        <p className="text-muted-foreground m-0 text-sm">
          Move every transaction from <strong>{account.name}</strong> to
          another account.
        </p>
        <InputGroup label="Destination account">
          <AccountSelect
            autoFocus
            excludeIds={[account._id]}
            selectedAccount={selectedAccount}
            onSelectAccount={handleSelectAccount}
            // The server blocks cross-currency migrations; don't offer them.
            currency={account.currency}
          />
        </InputGroup>

        {previewLoading && <Loading size="small" />}

        {previewError && (
          <p className="text-destructive m-0 text-sm">{previewError.message}</p>
        )}

        {preview && !previewLoading && (
          <div className="flex flex-col gap-3">
            <div className="text-foreground text-sm">
              {preview.count === 0 ? (
                <p className="m-0">
                  {preview.deletedCount > 0
                    ? `Only deleted transactions remain: ${preview.deletedCount.toLocaleString()} will move so nothing keeps referencing this account.`
                    : 'There are no transactions to migrate.'}
                </p>
              ) : (
                <p className="m-0">
                  {preview.count.toLocaleString()}{' '}
                  {preview.count === 1 ? 'transaction' : 'transactions'} (
                  {formatRange(preview.fromOldestDate, preview.fromNewestDate)}
                  ) will move.{' '}
                  {preview.toCount === 0
                    ? 'The destination account has no transactions.'
                    : `The destination account already has ${preview.toCount.toLocaleString()} ${
                        preview.toCount === 1 ? 'transaction' : 'transactions'
                      } (${formatRange(
                        preview.toOldestDate,
                        preview.toNewestDate,
                      )}).`}
                </p>
              )}
              {preview.count > 0 && preview.deletedCount > 0 && (
                <p className="m-0 mt-1">
                  {preview.deletedCount.toLocaleString()} deleted{' '}
                  {preview.deletedCount === 1 ? 'transaction' : 'transactions'}{' '}
                  will move too.
                </p>
              )}
              {preview.count > 0 && preview.feePeriodCount > 0 && (
                <p className="m-0 mt-1">
                  {preview.feePeriodCount === 1
                    ? '1 fee period and its card benefits move too.'
                    : `${preview.feePeriodCount} fee periods and their card benefits move too.`}
                </p>
              )}
              <p className="m-0 mt-1">
                {preview.snapshotCount > 0
                  ? `${preview.snapshotCount.toLocaleString()} portfolio ${
                      preview.snapshotCount === 1 ? 'snapshot moves' : 'snapshots move'
                    } too.`
                  : 'Balance observations stay on the source account.'}
              </p>
            </div>

            {preview.count > 0 && preview.timeline.length > 0 && (
              <MigrationTimeline
                unit={preview.timelineUnit}
                buckets={preview.timeline}
                fromName={account.name}
                toName={selectedAccount
                  .map((destination) => destination.name)
                  .getOrElse('Destination')}
                overlapStartDate={preview.overlapStartDate ?? null}
                overlapEndDate={preview.overlapEndDate ?? null}
              />
            )}

            {preview.blockers.length > 0 && (
              <div className="border-destructive/50 text-destructive rounded-md border p-3 text-sm">
                <p className="m-0 font-medium">
                  This migration can’t run yet:
                </p>
                <ul className="m-0 mt-1 list-disc pl-5">
                  {preview.blockers.map((blocker) => (
                    <li key={blocker}>{blocker}</li>
                  ))}
                </ul>
              </div>
            )}

            {preview.blockers.length === 0 && overlapWindow && (
              <div className="flex flex-col gap-2 rounded-md border border-amber-500/50 p-3 text-sm text-amber-600 dark:text-amber-400">
                <p className="m-0">
                  Both accounts have transactions between{' '}
                  <strong>{overlapWindow.start}</strong> and{' '}
                  <strong>{overlapWindow.end}</strong>. This usually means
                  the same history was imported into both accounts, so
                  migrating may create duplicates.
                </p>
                {/* Keyed by destination so the expanded/collapsed state and
                    fetched rows never carry over to a different account. */}
                <MigrationOverlapPreview
                  key={destinationId}
                  fromAccountId={account._id}
                  fromAccountName={account.name}
                  toAccountId={destinationId}
                  toAccountName={selectedAccount
                    .map((destination) => destination.name)
                    .getOrElse('Destination')}
                  overlapStartDate={overlapWindow.start}
                  overlapEndDate={overlapWindow.end}
                />
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={overlapConfirmed}
                    onChange={(e) => setOverlapConfirmed(e.target.checked)}
                    className="shrink-0"
                  />
                  I’ve reviewed the overlap and want to migrate anyway
                </label>
              </div>
            )}
          </div>
        )}

        {migrateError && (
          <p className="text-destructive m-0 text-sm">{migrateError.message}</p>
        )}
      </form>
    </Modal>
  );
};
